import { AxiosError } from 'axios';
import { OAuthTokens } from '@/types';
import { AsyncModule } from 'vuex-async-mutations';

interface AuthenticationState {
  tokens: {
    user: OAuthTokens | null;
    impersonation: OAuthTokens | null;
  };
}

const stateFactory = (): AuthenticationState => ({
  tokens: { user: null, impersonation: null },
});

export const module: AsyncModule<AuthenticationState, any> = {
  state: stateFactory,

  getters: {
    ['isAuthenticated'](state) {
      return !!state.tokens.user;
    },

    ['isImpersonated'](state) {
      return !!state.tokens.impersonation;
    },

    ['apiToken'](state) {
      if (state.tokens.impersonation) {
        return state.tokens.impersonation.access_token;
      }

      if (state.tokens.user) {
        return state.tokens.user.access_token;
      }

      return undefined;
    },

    ['tokens'](state) {
      return state.tokens;
    },
  },

  mutations: {
    ['signOut'](state) {
      state.tokens.user = null;
      state.tokens.impersonation = null;
    },

    ['signOutAs'](state) {
      state.tokens.impersonation = null;
    },

    ['reset'](state) {
      Object.assign(state, stateFactory());
    },
  },

  actionsAsync: {
    ['azureSignIn']: {
      async handler({ commitAsync, dispatch }, { azureToken }) {
        await commitAsync(
          this.$axios.$post(
            '/auth/azure-login',
            {},
            // The interceptor only replaces the header when it is `true`,
            // so the Azure AD token passes through to the backend as-is.
            { headers: { Authorization: `Bearer ${azureToken}` } },
          ),
        );

        return dispatch('ping');
      },

      pending(state) {
        state.tokens = { user: null, impersonation: null };
      },

      resolved(state, user: OAuthTokens) {
        state.tokens = { user, impersonation: null };
      },
    },

    ['ping']: {
      handler({ dispatch, commitAsync }) {
        return commitAsync(dispatch('authenticated', true, { root: true }));
      },

      // Only a rejected login signs out: offline or a server error must not, since signing out
      // wipes the work on this device that has not been saved yet.
      rejected(state, error: AxiosError) {
        if (error?.response?.status === 401) {
          state.tokens = { user: null, impersonation: null };
        }
      },
    },

    ['signInAs']: {
      async handler({ dispatch, commitAsync }, { email, pchn }) {
        await commitAsync(this.$axios.$post('/admin/impersonate', pchn ? { pchn } : { email }));

        return dispatch('ping');
      },

      pending(state) {
        state.tokens.impersonation = null;
      },

      resolved(state, tokens: OAuthTokens) {
        state.tokens.impersonation = tokens;
      },
    },
  },

  actions: {
    ['signOut']({ commit }) {
      commit('signOut');

      return this.dispatch('reset', Date.now());
    },

    ['signOutAs']({ commit, dispatch }) {
      commit('signOutAs');

      return dispatch('ping');
    },

    // "events"

    ['created']: {
      root: true,
      handler({ dispatch, getters }) {
        if (getters['isAuthenticated']) {
          return dispatch('ping');
        }

        return Promise.reject();
      },
    },
  },
};

export default module;
